'use client'

import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import { useToast } from '@/components/ui/use-toast'
import { useCreateEmailTemplate, useUpdateEmailTemplate, useEmailTemplatesList } from '@/lib/api/emailTemplates'
import { useStoreNavigate } from '@/hooks/use-app-navigate'
import type { EmailTemplateItem } from '@/lib/api/emailTemplates'
import { EmailTemplateEditor } from '../components/EmailTemplateEditor'
import { useParams } from 'react-router-dom'

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(CreateEmailTemplatePage, PERMISSIONS.STORES_MANAGE);

function CreateEmailTemplatePage() {
    const { t } = useTranslation(['emails', 'common'])
    const { t: tCommon } = useTranslation('common')
    const { storeRoutes, navigate, id: storeId } = useStoreNavigate()
    const { templateId } = useParams<{ templateId?: string }>()
    const { toast } = useToast()
    const isEditMode = !!templateId

    const createMutation = useCreateEmailTemplate()
    const updateMutation = useUpdateEmailTemplate()

    const { data: templatesData, isLoading: isLoadingTemplate } = useEmailTemplatesList(
        { storeId },
        { enabled: isEditMode && !!templateId && !!storeId },
    )

    const existingTemplate = isEditMode && templateId
        ? templatesData?.templates.find((t) => t.id === templateId)
        : null

    const [template] = React.useState<EmailTemplateItem | null>(() => {
        if (existingTemplate) return existingTemplate

        if (!isEditMode) {
            // Leave `type` undefined so the editor's empty state surfaces a
            // prominent "What is this email for?" picker — much more discoverable
            // than burying the selector inside the settings popover.
            return {
                id: '',
                name: '',
                type: undefined,
                storeId: storeId || '',
                accountId: '',
                active: true,
                createdAt: new Date(),
                updatedAt: new Date(),
                subject: {},
                params: {
                    replyTo: '',
                    specific: {},
                },
                templateVariables: {},
            } as unknown as EmailTemplateItem
        }
        return null
    })

    const handleSave = React.useCallback(
        async (data: Partial<EmailTemplateItem>) => {
            try {
                if (isEditMode && templateId) {
                    await updateMutation.mutateAsync({
                        id: templateId,
                        data: { ...data, storeId } as any,
                    })
                    // Stay on the page — merchants iterate quickly and we don't
                    // want to send them back to the list after every save.
                } else {
                    const created = await createMutation.mutateAsync({
                        ...data,
                        storeId,
                    } as any)
                    // After a successful CREATE, swap the URL for the canonical
                    // edit URL so subsequent saves are updates (and Test works).
                    const newId = (created as { id?: string } | undefined)?.id
                    if (newId) {
                        navigate(storeRoutes.EMAIL_TEMPLATE_EDIT(newId))
                    }
                }
            } catch (error: any) {
                toast({
                    title: tCommon('error'),
                    description: error.message || t('saveError'),
                    variant: 'destructive',
                })
            }
        },
        [isEditMode, templateId, storeId, createMutation, updateMutation, navigate, storeRoutes, toast, t, tCommon],
    )

    const handleTest = React.useCallback(
        async (email: string) => {
            if (!template?.id) {
                toast({
                    title: tCommon('info'),
                    description: t('saveFirst'),
                })
                return
            }
        },
        [template?.id, toast, t, tCommon],
    )

    if (isLoadingTemplate && isEditMode) {
        return (
            <div className="flex h-screen items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        )
    }

    if (!template) {
        return (
            <div className="flex h-screen items-center justify-center">
                <p className="text-muted-foreground">{t('templateNotFound')}</p>
            </div>
        )
    }

    return (
        <EmailTemplateEditor
            template={template}
            onSave={handleSave}
            onTest={handleTest}
            isLoading={createMutation.isPending || updateMutation.isPending}
            title={isEditMode
                ? t('editTemplate')
                : t('createTemplate')}
            subtitle={isEditMode
                ? t('editDescription')
                : t('createDescription')}
            backTo={storeRoutes.EMAILS()}
        />
    )
}
